import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { initAnalytics, trackPageview } from "@/lib/analytics";
import { initNativeBridge } from "@/lib/nativeBridge";
import { initTikTokPixel, trackPageView as ttPageView } from "@/lib/tiktokPixel";
import Seo from "@/components/Seo";
import ConsentBanner from "@/components/ConsentBanner";
import { PremiumComingSoonProvider } from "@/context/PremiumComingSoonContext";

/**
 * Per-route SEO metadata table.
 *
 * ONLY genuinely public, indexable routes carry positive `robots` and their
 * own <title>/<meta description>. Auth routes are shipped with
 * `noindex: true` so they never dilute the site's ranking signal. Redirect
 * routes get no Seo (they're transient).
 *
 * The homepage's <title> and <meta description> already exist in index.html
 * as a first-paint fallback for naive crawlers — Helmet then upgrades them
 * per-route once React hydrates.
 */
const SEO_PAGES = {
    home: {
        path: "/",
        title: "PawLink | Smart QR Pet ID & Lost Pet Recovery",
        description: "PawLink is the smart QR pet ID that reunites lost pets fast — instant scan alerts, finder GPS sharing, digital pet passport, and a live community rescue map.",
    },
    subscription: {
        path: "/subscription",
        title: "PawLink Protection — Subscribe to the Smart QR Pet Safety Plan",
        description: "Unlock PawLink Protection: instant scan alerts, full rescue card, live community map. From 9.90 CHF / month or 79.00 CHF / year.",
    },
    faq: {
        path: "/faq",
        title: "PawLink FAQ — How the Smart QR Pet ID Works",
        description: "Answers to common questions about PawLink smart QR pet tags: how scanning works, what happens when a finder scans your pet, pricing, privacy, and setup.",
    },
    install: {
        path: "/install",
        title: "Install PawLink — Add the Smart QR Pet Safety App to Your Phone",
        description: "Install PawLink on iPhone, Android, or desktop in one tap. Get instant scan alerts and manage your pet's rescue card from the home screen.",
    },
    support: {
        path: "/support",
        title: "PawLink Support — Contact the Pet Safety Team",
        description: "Get help with your PawLink account, QR tag, subscription, or Rescue Card. Fast, friendly support from the PawLink pet-safety team.",
    },
    privacy: {
        path: "/privacy-policy",
        title: "PawLink Privacy Policy",
        description: "How PawLink collects, uses, and protects your data — including pet profiles, finder GPS coordinates, and account information.",
    },
    terms: {
        path: "/terms-of-use",
        title: "PawLink Terms of Use",
        description: "The terms and conditions that govern your use of the PawLink smart QR pet ID service, PawLink Protection subscription, and the PawLink community rescue map.",
    },
    refunds: {
        path: "/refund-policy",
        title: "PawLink Refund Policy",
        description: "How PawLink handles refunds and cancellations for PawLink Protection subscriptions, including the 7-day free trial and post-trial billing.",
    },
    deleteAccount: {
        path: "/delete-account",
        title: "Delete Your PawLink Account",
        description: "Request permanent deletion of your PawLink account, pet profiles, and all associated data in accordance with our Privacy Policy.",
    },
};

// Small helper: wrap a page element with its Seo tags in one expression.
const withSeo = (seoKey, element, extra = {}) => (
    <>
        <Seo {...SEO_PAGES[seoKey]} {...extra} />
        {element}
    </>
);

// Noindex wrapper for auth routes (index them and Google will surface login
// pages instead of the marketing homepage — very bad).
const withNoindex = (title, path, element) => (
    <>
        <Seo title={title} description={SEO_PAGES.home.description} path={path} noindex />
        {element}
    </>
);
import ProtectedRoute from "@/components/ProtectedRoute";
import LanguagePicker from "@/components/LanguagePicker";
import ErrorBoundary from "@/components/ErrorBoundary";
import Home from "@/pages/Home";
import RescueCenter from "@/pages/RescueCenter";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import FAQ from "@/pages/FAQ";
import Support from "@/pages/Support";
import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PetForm from "@/pages/PetForm";
import Finder from "@/pages/Finder";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import PetsPage from "@/pages/PetsPage";
import PetQRPage from "@/pages/PetQRPage";
import PetSafetyPage from "@/pages/PetSafetyPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import MagicLogin from "@/pages/MagicLogin";
import CommunityMap from "@/pages/CommunityMap";
import LocalWalks from "@/pages/LocalWalks";
import LostPets from "@/pages/LostPets";
import LostPetDetail from "@/pages/LostPetDetail";
import RefundPolicy from "@/pages/RefundPolicy";
import Install from "@/pages/Install";
import DeleteAccount from "@/pages/DeleteAccount";
import { Toaster } from "sonner";

function RootRedirect() {
    const { user, ready } = useAuth();
    if (!ready) return null;
    // Logged-in users go to their dashboard; anonymous visitors see the public marketing landing.
    if (user) return <Navigate to="/home" replace />;
    return withSeo("home", <Home />);
}

function AppRouter() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Fire a GA4 page_view on every SPA route change (production-only; the
    // helper is a no-op elsewhere). Runs after commit so document.title reflects
    // the new route. Also fires TikTok Pixel PageView (no-op unless consent
    // was granted — see /src/lib/tiktokPixel.js).
    useEffect(() => {
        trackPageview(location.pathname + location.search, document.title);
        ttPageView();
    }, [location.pathname, location.search]);

    // Native (Capacitor Android) bridge — no-op on web/PWA.
    // Registers FCM push token and wires deep-link intents to in-app navigation.
    useEffect(() => {
        if (!user || user === false) return;
        initNativeBridge({
            getJwt: () => {
                try { return localStorage.getItem("pawlink_token"); } catch { return null; }
            },
            navigate: (path) => navigate(path),
        });
    }, [user, navigate]);

    // Synchronously detect Google OAuth return before ProtectedRoute renders
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/welcome" element={withNoindex("Welcome to PawLink", "/welcome", <Welcome />)} />
            <Route path="/login" element={withNoindex("Sign in to PawLink", "/login", <Login />)} />
            <Route path="/register" element={withNoindex("Create your PawLink account", "/register", <Register />)} />
            <Route path="/forgot-password" element={withNoindex("Reset your PawLink password", "/forgot-password", <ForgotPassword />)} />
            <Route path="/reset-password" element={withNoindex("Reset your PawLink password", "/reset-password", <ResetPassword />)} />
            <Route path="/magic-login" element={withNoindex("PawLink magic link sign-in", "/magic-login", <MagicLogin />)} />
            <Route path="/pricing" element={<Navigate to="/subscription" replace />} />
            <Route path="/payment/success" element={<Navigate to="/subscription" replace />} />
            <Route path="/p/:publicId" element={<Finder />} />
            {/* Lost Pets feature temporarily hidden pre-launch. Redirect to home; components preserved for later restoration. */}
            <Route path="/lost" element={<Navigate to="/" replace />} />
            <Route path="/lost/:publicId" element={<Navigate to="/" replace />} />
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/pets/new" element={<ProtectedRoute><PetForm /></ProtectedRoute>} />
            <Route path="/pets/:id/edit" element={<ProtectedRoute><PetForm /></ProtectedRoute>} />
            <Route path="/pets" element={<ProtectedRoute><PetsPage /></ProtectedRoute>} />
            <Route path="/qr" element={<Navigate to="/pets" replace />} />
            <Route path="/qr/:id" element={<ProtectedRoute><PetQRPage /></ProtectedRoute>} />
            <Route path="/pets/:id/safety" element={<ProtectedRoute><PetSafetyPage /></ProtectedRoute>} />
            <Route path="/rescue" element={<ProtectedRoute><RescueCenter /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute><CommunityMap /></ProtectedRoute>} />
            <Route path="/local-walks" element={<ProtectedRoute><LocalWalks /></ProtectedRoute>} />
            <Route path="/scans" element={<Navigate to="/rescue" replace />} />
            <Route path="/notifications" element={<Navigate to="/rescue" replace />} />
            {/* Subscription is PUBLIC — visitors can subscribe without an account.
                The account is auto-provisioned by the Paddle webhook after payment. */}
            <Route path="/subscription" element={withSeo("subscription", <SubscriptionPage />)} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/privacy-policy" element={withSeo("privacy", <PrivacyPolicy />)} />
            <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />
            <Route path="/terms-of-service" element={<Navigate to="/terms-of-use" replace />} />
            <Route path="/terms-of-use" element={withSeo("terms", <TermsOfUse />)} />
            <Route path="/refunds" element={<Navigate to="/refund-policy" replace />} />
            <Route path="/refund-policy" element={withSeo("refunds", <RefundPolicy />)} />
            <Route path="/download" element={<Navigate to="/install" replace />} />
            <Route path="/get" element={<Navigate to="/install" replace />} />
            <Route path="/install" element={withSeo("install", <Install />)} />
            <Route path="/delete-account" element={withSeo("deleteAccount", <DeleteAccount />)} />
            <Route path="/data-deletion" element={<Navigate to="/delete-account" replace />} />
            <Route path="/account-deletion" element={<Navigate to="/delete-account" replace />} />
            <Route path="/faq" element={withSeo("faq", <FAQ />)} />
            <Route path="/support" element={withSeo("support", <Support />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    // GA4 bootstrap. Runs once, guarded to production host only.
    useEffect(() => { initAnalytics(); }, []);
    // TikTok Pixel bootstrap. Subscribes to consent changes; only actually
    // injects the pixel + fires PageView after the user grants consent.
    useEffect(() => { initTikTokPixel(); }, []);

    return (
        <ErrorBoundary>
            <HelmetProvider>
                <I18nProvider>
                    <AuthProvider>
                        <PremiumComingSoonProvider>
                            <BrowserRouter>
                                <Toaster position="top-center" richColors />
                                <LanguagePicker />
                                <AppRouter />
                                <ConsentBanner />
                            </BrowserRouter>
                        </PremiumComingSoonProvider>
                    </AuthProvider>
                </I18nProvider>
            </HelmetProvider>
        </ErrorBoundary>
    );
}
